import { useMemo } from 'preact/hooks';
import {
  AlertTriangle,
  BatteryLow,
  Flag,
  FlagTriangleRight,
  Navigation,
  Timer,
  UserPlus,
  type LucideIcon,
} from 'lucide-preact';
import { routeStore } from '../state/routeStore';
import { routeEventKey } from '../backend/routeMerge';
import { t } from '../i18n';
import { formatWaypointClock } from '../lib/activeWaypoint';
import { describeEvent, visibleJourneyEvents, type RouteEventType } from '../lib/journeyEvents';

const ICONS: Record<RouteEventType, LucideIcon> = {
  route_started: Flag,
  battery_drop: BatteryLow,
  stationary: Timer,
  companion_joined: UserPlus,
  still_en_route: Navigation,
  arrived: FlagTriangleRight,
  emergency: AlertTriangle,
};

/**
 * Row model for the journey log — the events the API detects along the way
 * (#180): start, battery drops, stationary phases, companions joining,
 * en-route heartbeats, arrival, emergencies. Initial rows come from the
 * route read's `events[]`; live rows append via the `route-event` socket
 * listener. Returns an empty list on routes without describable events, so
 * pre-#180 journeys can hide the log entirely.
 */
export function useJourneyRows() {
  const route = routeStore.value;
  const events = route?.events;
  const alertedCount = route?.emergency?.alertedCount;
  // Memoized on the events array's identity: waypoint appends replace the
  // route object every few seconds but preserve `events` — only an actual
  // event append or poll union should redo the sort/describe. t()/clock
  // formatting stay in the JSX below so locale switches re-render.
  return useMemo(() => {
    if (!events || events.length === 0) return [];
    const visible = visibleJourneyEvents(events);
    // alertedCount describes the current emergency only — attach it to the
    // latest emergency row, not to historical ones from earlier triggers.
    let lastEmergency = -1;
    visible.forEach((event, index) => {
      if (event.type === 'emergency') lastEmergency = index;
    });
    return visible
      .map((event, index) => {
        const description = describeEvent(
          event,
          index === lastEmergency ? alertedCount : undefined,
        );
        return description ? { key: routeEventKey(event), description } : null;
      })
      .filter((row) => row !== null);
  }, [events, alertedCount]);
}

/**
 * The journey-log list body. Rendered inside the log overlay (see
 * JourneyLog), which owns its own title — so this is just the `<ol>`.
 * Renders nothing when there are no describable events.
 */
export function JourneyTimeline() {
  const rows = useJourneyRows();
  if (rows.length === 0) return null;
  return (
    <section aria-label={t('timeline.title')}>
      <ol class="space-y-2 text-sm">
        {rows.map(({ key, description }) => {
          const Icon = ICONS[description.type];
          const clock = formatWaypointClock(description.time ?? undefined);
          const emphasis = description.type === 'emergency';
          return (
            <li key={key} class="flex items-center gap-2.5 rounded-xl bg-surface-muted px-3 py-2">
              <span class={emphasis ? 'text-emergency shrink-0' : 'text-primary shrink-0'} aria-hidden="true">
                <Icon size={14} />
              </span>
              <span class={`min-w-0 flex-1 leading-snug break-words ${emphasis ? 'font-medium text-emergency' : 'text-text'}`}>
                {t(description.labelKey, description.vars)}
              </span>
              {clock && (
                <span class="text-xs text-text-secondary tabular-nums shrink-0">{clock}</span>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
